import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").trim();
const FRONTEND_URL =
  process.env.REACT_APP_FRONT_END_URL ||
  "https://abtesting-frontend-madhavans-projects-3955d847.vercel.app";

export default function CreateExperiment() {
  const [form, setForm] = useState({
    testName: "",
    pageUrl: "",
    selector: "",
    goalSelector: "",
    splitA: 50,
    splitB: 50,
    durationDays: 7,
    mode: "manual",
  });

  const [snippet, setSnippet] = useState("");
  const [createdExperiment, setCreatedExperiment] = useState(null);
  const [copyStatus, setCopyStatus] = useState("Copy");
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setForm({
      ...form,
      [name]: type === "number" ? Number(value) : value,
    });
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopyStatus("Copied!");
      setTimeout(() => setCopyStatus("Copy"), 2000);
    } catch (err) {
      alert("Failed to copy text");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (form.splitA + form.splitB !== 100) {
      alert("Split A and Split B must add up to 100%");
      return;
    }

    const userId = localStorage.getItem("user_id");
    if (!userId) {
      alert("User not logged in");
      return;
    }

    const payload = {
      ...form,
      user_id: Number(userId),
      type: form.mode,
    };

    const response = await fetch(`${BACKEND_URL}/api/experiments/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json();
      setSnippet(""); // Clear old snippet if a new submission fails (e.g., duplicate name)
      setCreatedExperiment(null);
      alert("Error: " + (errorData.detail || "Unknown error"));
      return;
    }

    const data = await response.json();

    // LOGIC: Select script based on mode
    const scriptFile = form.mode === "editor" ? "ab1.js" : "ab.js";

    const jsSnippet = `<script>
  (function() {
    var script = document.createElement('script');
    script.src = "${FRONTEND_URL}/${scriptFile}?test_id=${data.testId}";
    script.async = true;
    document.head.appendChild(script);
  })();
</script>`;

    if (form.mode === "manual") {
      setSnippet(jsSnippet);
      setCreatedExperiment(null);
      return;
    }

    setSnippet("");
    setCreatedExperiment({
      testId: data.testId,
      pageUrl: form.pageUrl,
      mode: form.mode,
      createdAt: new Date().toLocaleString(),
      setupStatus: "Step 1 (Draft)",
    });
  };

  const handleBack = () => navigate("/dashboard");

  const renderInput = ({ label, name, type, min, max, required }) => {
    const isEditorDisabled =
      form.mode === "editor" &&
      (name === "selector" || name === "goalSelector");

    return (
      <div
        key={name}
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: 14,
          gap: 12,
        }}
      >
        <label
          htmlFor={name}
          style={{
            width: 150,
            fontWeight: 600,
            fontSize: 14,
            color: "#374151",
            display: "flex",
            alignItems: "center",
          }}
        >
          {label}
          {(name === "selector" || name === "goalSelector") && (
            <Tooltip
              title={
                name === "selector"
                  ? "Choose the CSS element where the experiment applies."
                  : "This element’s interaction will be tracked as your conversion goal."
              }
              placement="right"
              arrow
            >
              <HelpOutlineIcon
                style={{
                  fontSize: 16,
                  marginLeft: 6,
                  cursor: "pointer",
                  color: "#2563eb",
                }}
              />
            </Tooltip>
          )}
        </label>

        <input
          id={name}
          type={type}
          name={name}
          value={form[name]}
          onChange={handleChange}
          min={min}
          max={max}
          required={required && form.mode === "manual"}
          disabled={isEditorDisabled}
          style={{
            flex: 1,
            padding: "8px 10px",
            fontSize: 14,
            borderRadius: 6,
            border: "1.5px solid #d1d5db",
            backgroundColor: isEditorDisabled ? "#f3f4f6" : "#fff",
            opacity: isEditorDisabled ? 0.6 : 1,
            outline: "none",
          }}
        />
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#f3f4f6] text-slate-900 py-12 px-6">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(67,179,126,0.08),_transparent_40%),radial-gradient(circle_at_top_right,_rgba(59,130,246,0.05),_transparent_35%)] pointer-events-none" />
      
      <div className="relative max-w-4xl mx-auto">
        {/* Header Section */}
        <div className="flex justify-between items-start mb-10">
          <div>
            <h1 className="text-4xl font-black text-slate-900">🎯 Create New Experiment</h1>
            <p className="text-slate-600 mt-2">Set up a new A/B test and get your tracking snippet</p>
          </div>
          <div className="flex gap-3">
            <Link to="/FAQ">
              <button className="px-5 py-3 rounded-lg bg-[#8ff0b3] text-[#0f2b24] font-bold hover:bg-[#b3f7ca] transition shadow-lg shadow-emerald-500/20">
                FAQ
              </button>
            </Link>
            <button
              onClick={handleBack}
              className="px-5 py-3 rounded-lg border border-slate-300 bg-white text-slate-800 font-semibold hover:bg-slate-100 hover:border-slate-400 transition"
            >
              Back
            </button>
          </div>
        </div>

        {/* Main Form Section */}
        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-lg shadow-black/5 mb-8">
          <form onSubmit={handleSubmit}>
            {/* Experiment Type Selector */}
            <div className="mb-8 pb-8 border-b border-slate-200">
              <label className="block text-sm font-bold uppercase tracking-wider text-emerald-700 mb-4">
                Experiment Type
              </label>
              <div className="flex gap-6">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="radio"
                    name="mode"
                    value="manual"
                    checked={form.mode === "manual"}
                    onChange={handleChange}
                    className="w-4 h-4 cursor-pointer"
                  />
                  <span className="text-slate-900 font-semibold group-hover:text-emerald-700 transition">
                    Manual (Custom Selector)
                  </span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="radio"
                    name="mode"
                    value="editor"
                    checked={form.mode === "editor"}
                    onChange={handleChange}
                    className="w-4 h-4 cursor-pointer"
                  />
                  <span className="text-slate-900 font-semibold group-hover:text-emerald-700 transition">
                    Visual Editor
                  </span>
                </label>
              </div>
            </div>

            {/* Form Fields */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Test Name *</label>
                <input
                  type="text"
                  name="testName"
                  value={form.testName}
                  onChange={handleChange}
                  placeholder="e.g., Homepage CTA Test"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 bg-white text-slate-900 placeholder-slate-400 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Page URL *</label>
                <input
                  type="url"
                  name="pageUrl"
                  value={form.pageUrl}
                  onChange={handleChange}
                  placeholder="https://example.com"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 bg-white text-slate-900 placeholder-slate-400 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition"
                />
              </div>

              <div>
                <label className="flex text-sm font-semibold text-slate-700 mb-2 items-center gap-2">
                  Selector *
                  <Tooltip
                    title="Choose the CSS element where the experiment applies."
                    placement="right"
                    arrow
                  >
                    <HelpOutlineIcon style={{ fontSize: 16, cursor: "pointer", color: "#059669" }} />
                  </Tooltip>
                </label>
                <input
                  type="text"
                  name="selector"
                  value={form.selector}
                  onChange={handleChange}
                  placeholder="e.g., .cta-button or #hero-section"
                  required={form.mode === "manual"}
                  disabled={form.mode === "editor"}
                  className={`w-full px-4 py-3 rounded-lg border transition ${
                    form.mode === "editor"
                      ? "border-slate-300 bg-slate-100 text-slate-400 opacity-60 cursor-not-allowed"
                      : "border-slate-300 bg-white text-slate-900 placeholder-slate-400 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                  }`}
                />
              </div>

              <div>
                <label className="flex text-sm font-semibold text-slate-700 mb-2 items-center gap-2">
                  Goal Selector *
                  <Tooltip
                    title="This element's interaction will be tracked as your conversion goal."
                    placement="right"
                    arrow
                  >
                    <HelpOutlineIcon style={{ fontSize: 16, cursor: "pointer", color: "#059669" }} />
                  </Tooltip>
                </label>
                <input
                  type="text"
                  name="goalSelector"
                  value={form.goalSelector}
                  onChange={handleChange}
                  placeholder="e.g., .signup-btn"
                  required={form.mode === "manual"}
                  disabled={form.mode === "editor"}
                  className={`w-full px-4 py-3 rounded-lg border transition ${
                    form.mode === "editor"
                      ? "border-slate-300 bg-slate-100 text-slate-400 opacity-60 cursor-not-allowed"
                      : "border-slate-300 bg-white text-slate-900 placeholder-slate-400 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                  }`}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Split A (%)</label>
                <input
                  type="number"
                  name="splitA"
                  value={form.splitA}
                  onChange={handleChange}
                  min="0"
                  max="100"
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 bg-white text-slate-900 placeholder-slate-400 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition"
                />
                <p className="text-xs text-slate-400 mt-1">Traffic allocated to version A</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Split B (%)</label>
                <input
                  type="number"
                  name="splitB"
                  value={form.splitB}
                  onChange={handleChange}
                  min="0"
                  max="100"
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 bg-white text-slate-900 placeholder-slate-400 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition"
                />
                <p className="text-xs text-slate-400 mt-1">Traffic allocated to version B</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Duration (Days)</label>
                <input
                  type="number"
                  name="durationDays"
                  value={form.durationDays}
                  onChange={handleChange}
                  min="1"
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 bg-white text-slate-900 placeholder-slate-400 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition"
                />
                <p className="text-xs text-slate-400 mt-1">How long to run the test</p>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                className="px-8 py-4 rounded-lg bg-[#8ff0b3] text-[#0f2b24] font-bold text-base hover:bg-[#b3f7ca] transition shadow-lg shadow-emerald-500/20 flex items-center gap-2"
              >
                <span>🚀 Create Experiment</span>
              </button>
            </div>
          </form>
        </div>

        {/* Manual Mode: Snippet Section */}
        {snippet && (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-lg shadow-black/5 animate-in fade-in">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-xl font-bold text-slate-900">📋 Embed JS Snippet</h3>
                <p className="text-slate-600 text-sm mt-1">Add this code to your website's &lt;head&gt; or &lt;body&gt;</p>
              </div>
              <button
                onClick={handleCopy}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#8ff0b3] text-[#0f2b24] font-semibold hover:bg-[#b3f7ca] transition"
              >
                <ContentCopyIcon style={{ fontSize: 16 }} />
                {copyStatus}
              </button>
            </div>

            <div className="rounded-lg overflow-hidden border border-slate-300 bg-slate-900">
              <pre className="text-emerald-400 p-6 text-sm overflow-x-auto font-mono leading-relaxed">
                {snippet}
              </pre>
            </div>

            <div className="mt-6 p-4 rounded-lg bg-emerald-50 border border-emerald-200">
              <p className="text-emerald-800 text-sm">
                ✅ Snippet created successfully! Paste this code on your website and your experiment will start tracking.
              </p>
            </div>
          </div>
        )}

        {/* Editor Mode: Draft Status Section */}
        {createdExperiment && createdExperiment.mode === "editor" && (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-lg shadow-black/5 animate-in fade-in">
            <div className="mb-6">
              <h3 className="text-xl font-bold text-slate-900">Experiment Created (Draft)</h3>
              <p className="text-slate-600 text-sm mt-1">
                Setup is incomplete. Complete variation and goal selection in Editor to finalize the script.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs uppercase tracking-wider text-slate-500 font-bold">Experiment ID</p>
                <p className="text-slate-900 font-semibold mt-1">{createdExperiment.testId}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs uppercase tracking-wider text-slate-500 font-bold">Setup Status</p>
                <p className="text-slate-900 font-semibold mt-1">{createdExperiment.setupStatus}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 md:col-span-2">
                <p className="text-xs uppercase tracking-wider text-slate-500 font-bold">Page URL</p>
                <p className="text-slate-900 font-semibold mt-1 break-all">{createdExperiment.pageUrl}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 md:col-span-2">
                <p className="text-xs uppercase tracking-wider text-slate-500 font-bold">Created At</p>
                <p className="text-slate-900 font-semibold mt-1">{createdExperiment.createdAt}</p>
              </div>
            </div>

            <div className="rounded-lg overflow-hidden border border-slate-300 bg-slate-900 mb-6">
              <pre className="text-slate-400 p-6 text-sm overflow-x-auto font-mono leading-relaxed">
                Script will be available after Editor setup is completed.
              </pre>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => navigate(`/editor/${createdExperiment.testId}`)}
                className="px-6 py-3 rounded-lg bg-[#8ff0b3] text-[#0f2b24] font-bold hover:bg-[#b3f7ca] transition shadow-lg shadow-emerald-500/20"
              >
                Open in Editor
              </button>
              <button
                onClick={() => navigate("/viewexperiment")}
                className="px-6 py-3 rounded-lg border border-slate-300 bg-white text-slate-800 font-semibold hover:bg-slate-100 hover:border-slate-400 transition"
              >
                Go to View Experiments
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
